/**
 * Чиста логіка персональних налаштувань списку (колонки таблиці + поля фільтра).
 *
 * Тут немає React/MobX — лише білдери ключів user-option та злиття (merge)
 * збереженого користувацького конфігу з актуальним набором ресурсу. Винесено
 * окремо, щоб покрити тестами (в проєкті тестуються Stores/Tasks/Helpers).
 *
 * Ключова ідея — merge, а не replace: набір колонок/полів ресурсу з часом
 * змінюється, тож збережені налаштування накладаються на актуальний набір:
 *   - записи, що є і в конфігу, і в ресурсі → у збереженому порядку;
 *   - нові (є в ресурсі, немає в конфігу) → у кінець з дефолтами (visible: true);
 *   - застарілі (є в конфігу, немає в ресурсі) → ігноруються.
 */

/** Збережене налаштування однієї колонки */
export interface ISavedColumnSetting {
    key: string
    visible: boolean
    width: number | null
}

/** Значення user-option `tableColumns:${resource.key}` */
export interface ITableColumnsOption {
    columns: ISavedColumnSetting[]
}

/** Збережене налаштування одного поля фільтра */
export interface ISavedFilterFieldSetting {
    key: string
    visible: boolean
}

/** Значення user-option `filterFields:${resource.key}` */
export interface IFilterFieldsOption {
    fields: ISavedFilterFieldSetting[]
}

/** Колонка після злиття — повний доступний набір для Drawer та рендера */
export interface IResolvedColumn {
    key: string
    /** Оригінальний antd-опис колонки (title/dataIndex/render/...) */
    column: any
    /** Людиночитабельна назва для UI налаштувань */
    label: string
    visible: boolean
    width: number | null
}

/** Поле фільтра після злиття — повний доступний набір для Drawer та рендера */
export interface IResolvedFilterField {
    key: string
    label: string
    visible: boolean
}

/** Максимальна довжина коду опції на бекенді */
const MAX_OPTION_CODE_LENGTH = 50

/** Префікс кодів глобальних дефолтів ресурсу (сутність Option, пише лише адмін) */
const DEFAULT_OPTION_CODE_PREFIX = 'default_'

/**
 * Ліміт довжини ПЕРСОНАЛЬНОГО коду: сервер резолвить дефолт як default_<code>,
 * тож персональний код обрізаємо так, щоб і префіксований дефолтний код вкладався
 * в бекендові 50 символів. Інакше для довгих ключів ресурсу збережений дефолт
 * (обрізаний до 50) не збігався б із кодом, який сервер шукає при резолві.
 */
const MAX_PERSONAL_OPTION_CODE_LENGTH = MAX_OPTION_CODE_LENGTH - DEFAULT_OPTION_CODE_PREFIX.length

/**
 * Приводить ключ до припустимого коду user-option бекенду.
 * Бекенд дозволяє лише символи [a-z0-9_-] завдовжки 1..50, тож знижуємо регістр,
 * недозволені символи замінюємо на '_' і обрізаємо до ліміту (42 — щоб код
 * лишався валідним і з префіксом default_).
 * (Формат із таску `tableColumns:${key}` містить `:`/`.`/великі літери — бекенд його відхиляє.)
 */
const sanitizeOptionCode = (value: string): string =>
    String(value)
        .toLowerCase()
        .replace(/[^a-z0-9_-]/g, '_')
        .slice(0, MAX_PERSONAL_OPTION_CODE_LENGTH)

/** Ключ user-option для колонок таблиці конкретного ресурсу */
export const getTableColumnsOptionKey = (resourceKey: string): string => sanitizeOptionCode(`tablecolumns_${resourceKey}`)

/** Ключ user-option для полів фільтра конкретного ресурсу */
export const getFilterFieldsOptionKey = (resourceKey: string): string => sanitizeOptionCode(`filterfields_${resourceKey}`)

/**
 * Код глобального дефолту колонок (для POST/DELETE /services/options адміном).
 * Для ЧИТАННЯ через resolved передається звичайний ключ — префікс додає сервер.
 */
export const getDefaultTableColumnsOptionKey = (resourceKey: string): string =>
    `${DEFAULT_OPTION_CODE_PREFIX}${getTableColumnsOptionKey(resourceKey)}`

/** Код глобального дефолту полів фільтра (для POST/DELETE /services/options адміном) */
export const getDefaultFilterFieldsOptionKey = (resourceKey: string): string =>
    `${DEFAULT_OPTION_CODE_PREFIX}${getFilterFieldsOptionKey(resourceKey)}`

/**
 * Базовий (актуальний) набір колонок таблиці для ресурсу.
 * Живе в табличному варіанті списку, який будує AddTableViewModeToList.
 */
export const getBaseColumns = (crudContext: any): any[] => {
    return crudContext?.listContext?.variantItems?.table?.props?.columns ?? []
}

/**
 * Базовий (актуальний) набір полів фільтра ({ key, label }).
 * Враховує обмеження розробника через includeFields, інакше — усі поля фільтра.
 *
 * FormStore.fields — це Map<string, BaseFieldStore>, тож дістаємо саме значення
 * (з fallback на масив/об'єкт для сумісності).
 */
export const getBaseFilterFields = (filterFormContext: any): Array<{ key: string; label: string }> => {
    const fieldsSource = filterFormContext?.fields

    let allFields: any[] = []
    if (Array.isArray(fieldsSource)) allFields = fieldsSource
    else if (fieldsSource && typeof fieldsSource.values === 'function') allFields = Array.from(fieldsSource.values())
    else if (fieldsSource) allFields = Object.values(fieldsSource)

    const fieldByName: Record<string, any> = {}
    for (const field of allFields) fieldByName[field.name] = field

    const developerInclude = filterFormContext?.props?.includeFields
    if (Array.isArray(developerInclude)) {
        return developerInclude
            .filter((name: string) => fieldByName[name])
            .map((name: string) => ({ key: name, label: fieldByName[name]?.label ?? name }))
    }

    return allFields.map((field: any) => ({ key: field.name, label: field.label ?? field.name }))
}

/** Ключ antd-колонки (key має пріоритет над dataIndex) */
const getColumnKey = (column: any): string | undefined => {
    if (!column) return undefined
    const key = column.key ?? column.dataIndex
    return key !== undefined && key !== null ? String(key) : undefined
}

/** Назва колонки для UI: беремо title лише якщо це рядок, інакше dataIndex/key */
const getColumnLabel = (column: any, key: string): string => {
    if (typeof column?.title === 'string' && column.title.length > 0) return column.title
    if (column?.dataIndex !== undefined && column?.dataIndex !== null) return String(column.dataIndex)
    return key
}

/** Нормалізуємо ширину: додатне число або null (null → авто) */
const normalizeWidth = (width: unknown): number | null => {
    return typeof width === 'number' && Number.isFinite(width) && width > 0 ? width : null
}

/**
 * Зливає збережені налаштування колонок з актуальним набором.
 * @param baseColumns - актуальні колонки ресурсу (antd-описи)
 * @param saved - збережене значення user-option (може бути undefined)
 * @returns повний упорядкований набір колонок (включно з прихованими)
 */
export const resolveColumnSettings = (
    baseColumns: any[] | undefined,
    saved: ITableColumnsOption | undefined | null,
): IResolvedColumn[] => {
    const base = Array.isArray(baseColumns) ? baseColumns : []

    // Мапа актуальних колонок за ключем (лише ті, що мають ключ)
    const baseByKey = new Map<string, any>()
    for (const column of base) {
        const key = getColumnKey(column)
        if (key !== undefined && !baseByKey.has(key)) baseByKey.set(key, column)
    }

    const resolved: IResolvedColumn[] = []
    const used = new Set<string>()

    // 1. Збережені записи у своєму порядку (лише ті, що ще є в ресурсі)
    if (saved && Array.isArray(saved.columns)) {
        for (const item of saved.columns) {
            const key = item?.key
            if (key === undefined || used.has(key) || !baseByKey.has(key)) continue
            const column = baseByKey.get(key)
            resolved.push({
                key,
                column,
                label: getColumnLabel(column, key),
                visible: item.visible !== false,
                width: normalizeWidth(item.width) ?? normalizeWidth(column?.width),
            })
            used.add(key)
        }
    }

    // 2. Нові колонки (є в ресурсі, немає в конфігу) → у кінець з дефолтами
    for (const column of base) {
        const key = getColumnKey(column)
        if (key === undefined || used.has(key)) continue
        resolved.push({
            key,
            column,
            label: getColumnLabel(column, key),
            visible: true,
            width: normalizeWidth(column?.width),
        })
        used.add(key)
    }

    return resolved
}

/**
 * Перетворює зрезолвлений набір колонок на масив для рендера antd Table:
 * лишаємо лише видимі, зберігаємо порядок, застосовуємо width (null → авто).
 */
export const toRenderColumns = (resolved: IResolvedColumn[]): any[] => {
    return resolved
        .filter((item) => item.visible)
        .map((item) => (item.width !== null ? { ...item.column, width: item.width } : { ...item.column }))
}

/** Серіалізує зрезолвлений набір колонок назад у значення user-option */
export const serializeColumnSettings = (resolved: IResolvedColumn[]): ITableColumnsOption => ({
    columns: resolved.map((item) => ({ key: item.key, visible: item.visible, width: item.width })),
})

/**
 * Зливає збережені налаштування полів фільтра з актуальним набором.
 * @param baseFields - актуальні поля фільтра ({ key, label })
 * @param saved - збережене значення user-option (може бути undefined)
 * @returns повний упорядкований набір полів (включно з прихованими)
 */
export const resolveFilterFieldSettings = (
    baseFields: Array<{ key: string; label?: string }> | undefined,
    saved: IFilterFieldsOption | undefined | null,
): IResolvedFilterField[] => {
    const base = Array.isArray(baseFields) ? baseFields : []

    const baseByKey = new Map<string, { key: string; label?: string }>()
    for (const field of base) {
        if (field?.key !== undefined && !baseByKey.has(field.key)) baseByKey.set(field.key, field)
    }

    const resolved: IResolvedFilterField[] = []
    const used = new Set<string>()

    // 1. Збережені записи у своєму порядку (лише актуальні)
    if (saved && Array.isArray(saved.fields)) {
        for (const item of saved.fields) {
            const key = item?.key
            if (key === undefined || used.has(key)) continue
            const field = baseByKey.get(key)
            if (field === undefined) continue
            resolved.push({ key, label: field.label ?? key, visible: item.visible !== false })
            used.add(key)
        }
    }

    // 2. Нові поля → у кінець видимими
    for (const field of base) {
        if (field?.key === undefined || used.has(field.key)) continue
        resolved.push({ key: field.key, label: field.label ?? field.key, visible: true })
        used.add(field.key)
    }

    return resolved
}

/** Впорядкований список ключів видимих полів фільтра (для includeFields) */
export const toVisibleFieldKeys = (resolved: IResolvedFilterField[]): string[] => {
    return resolved.filter((item) => item.visible).map((item) => item.key)
}

/** Серіалізує зрезолвлений набір полів фільтра назад у значення user-option */
export const serializeFilterFieldSettings = (resolved: IResolvedFilterField[]): IFilterFieldsOption => ({
    fields: resolved.map((item) => ({ key: item.key, visible: item.visible })),
})
